import React, { useCallback, useEffect, useState } from 'react';

import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ImpressionElement, click, impression } from '@tracking';

import TableFilterButtons from './TableFilterButtons';
import TableLayout from './TableLayout';
import { TRUNCATED_MONTH, TableViews, TableViewsAsIndex } from './constants';
import { TechSentimentData } from './interfaces';

interface TableProps {
  techSentimentData: TechSentimentData;
}

const truncateMonth = (month: string): string => {
  return TRUNCATED_MONTH[month] ? TRUNCATED_MONTH[month] : month;
};

const transformHeaderText = (key: string, traversedYears: string[]): string => {
  const [headerText, year] = key.split('_');
  if (traversedYears.includes(year)) {
    return truncateMonth(headerText);
  }
  traversedYears.push(year);
  return `${truncateMonth(headerText)} ${year}`;
};

const transformedNetSentiment = (score: number): JSX.Element => {
  if (score > 0) {
    return <span className="before:pr-4 before:content-['\2191']">{score}%</span>;
  }
  return (
    <span className="before:pr-4 before:content-['\2193']">
      <span className="hidden">-</span>
      {score * -1}%
    </span>
  );
};

const Table = ({ techSentimentData }: TableProps): JSX.Element => {
  const [differenceHeaders, setDifferenceHeaders] = useState(null);
  const [differenceRows, setDifferenceRows] = useState(null);
  const [optimisticHeaders, setOptimisticHeaders] = useState(null);
  const [optimisticRows, setOptimisticRows] = useState(null);
  const [pessimisticHeaders, setPessimisticHeaders] = useState(null);
  const [pessimisticRows, setPessimisticRows] = useState(null);
  const [currentView, setCurrentView] = useState(TableViews.DIFFERENCE);
  const [isLoading, setIsLoading] = useState(true);
  const { trackEvent } = useEventTracking();

  const trackClickSort = useCallback(
    (columnName: string) => {
      trackEvent(
        click({
          clickTarget: ClickTarget.SortColumn,
          clickText: columnName,
          contentIndex: TableViewsAsIndex[currentView],
        })
      );
    },
    [currentView, trackEvent]
  );

  const createHeaders = useCallback(
    (view: TableViews): JSX.Element[] => {
      const currentData = techSentimentData[view];
      const traversedYears: string[] = [];

      return Object.keys(currentData[0]).map((colKey: string, colIndex: number) => (
        <th key={`${colKey}-${colIndex}`} className="center" onClick={() => trackClickSort(colKey)}>
          {colIndex === 0
            ? colKey.replaceAll('_', ' ')
            : transformHeaderText(colKey, traversedYears)}
        </th>
      ));
    },
    [techSentimentData, trackClickSort]
  );

  const createRows = useCallback(
    (view: TableViews): JSX.Element[] => {
      const currentData = techSentimentData[view];
      return currentData.map((rowValues, index) => (
        <tr key={`row-${index}`}>
          {Object.values(rowValues).map((colValue: string | number, colIndex: number) => (
            <td key={`${colValue}-${colIndex}`} className={colIndex === 0 ? 'left' : 'center'}>
              {colIndex === 0 ? (
                <div className="flex w-[135px] flex-row items-center justify-start gap-[14px] font-medium">
                  <div className={`company-logo company-logo__${colValue}`} />
                  {colValue}
                </div>
              ) : (
                <>{colIndex === 1 ? transformedNetSentiment(Number(colValue)) : colValue}</>
              )}
            </td>
          ))}
        </tr>
      ));
    },
    [techSentimentData]
  );

  useEffect(() => {
    if (!techSentimentData) return;

    global.$(function TechSentimentTracker() {
      if (isLoading) {
        trackEvent(impression({ impressionElement: ImpressionElement.Project }));
      }
      setDifferenceHeaders(createHeaders(currentView));
      setDifferenceRows(createRows(currentView));
      setOptimisticHeaders(createHeaders(TableViews.OPTIMISTIC));
      setOptimisticRows(createRows(TableViews.OPTIMISTIC));
      setPessimisticHeaders(createHeaders(TableViews.PESSIMISTIC));
      setPessimisticRows(createRows(TableViews.PESSIMISTIC));
      setIsLoading(false);
    });
  }, [
    createHeaders,
    createRows,
    currentView,
    isLoading,
    setIsLoading,
    techSentimentData,
    trackEvent,
  ]);

  return (
    <>
      {isLoading ? (
        <CustomSpinner
          style={{
            display: 'block',
            height: '15px',
            marginTop: '48px',
            marginLeft: 'auto',
            marginRight: 'auto',
            width: '48px',
          }}
          color={CustomSpinnerColor.PrimaryMagenta}
        />
      ) : (
        <TableFilterButtons currentView={currentView} setCurrentView={setCurrentView} />
      )}
      <TableLayout
        headers={differenceHeaders}
        rows={differenceRows}
        isHidden={currentView !== TableViews.DIFFERENCE}
        viewName={TableViews.DIFFERENCE}
      />
      <TableLayout
        headers={optimisticHeaders}
        rows={optimisticRows}
        isHidden={currentView !== TableViews.OPTIMISTIC}
        viewName={TableViews.OPTIMISTIC}
      />
      <TableLayout
        headers={pessimisticHeaders}
        rows={pessimisticRows}
        isHidden={currentView !== TableViews.PESSIMISTIC}
        viewName={TableViews.PESSIMISTIC}
      />
    </>
  );
};

export default Table;
